import { getShellLogger } from '../../common/logger'
import { createWebSocket } from './helpers'

export interface WebSocketPingRequest {
  readonly command: string
  readonly correlationId: number
}

export abstract class ShellWebSocket {
  protected correlationId = 0
  protected pingRequests = new Map<number, WebSocketPingRequest>()
  protected isTerminated = false

  private ws: WebSocket | null = null

  isConnecting(): boolean {
    if (this.ws) {
      return this.ws.readyState === WebSocket.CONNECTING
    }
    return false
  }

  isOpen(): boolean {
    if (this.ws) {
      return this.ws.readyState === WebSocket.OPEN
    }
    return false
  }

  isClosed(): boolean {
    if (this.ws) {
      return [WebSocket.CLOSED, WebSocket.CLOSING].includes(this.ws.readyState)
    }
    return true
  }

  disconnect() {
    if (this.isClosed()) {
      return
    }
    try {
      this.isTerminated = true
      this.ws?.close()
    } catch (error) {
      getShellLogger().error('WebSocket:Error trying to close WebSocket', error)
    }
    this.ws = null
  }

  protected ping(): void {
    const request: WebSocketPingRequest = {
      correlationId: this.correlationId++,
      command: 'ping',
    }

    this.pingRequests.set(request.correlationId, request)
    try {
      this.ws?.send(JSON.stringify(request))
    } catch (error) {
      getShellLogger().error('WebSocket:Ping fail to execute', error)
    }
  }

  protected handlePongMessage(correlationId?: number) {
    if (typeof correlationId !== 'undefined') {
      this.pingRequests.delete(correlationId)
    } else {
      this.pingRequests.clear()
    }
  }

  protected isPongMessage(correlationId: number): boolean {
    return this.pingRequests.has(correlationId)
  }

  protected internalConnect(url: string) {
    this.ws = createWebSocket(url)
    this.ws.onopen = this.onOpen.bind(this)
    this.ws.onclose = this.onClose.bind(this)
    this.ws.onerror = this.onError.bind(this)
    this.ws.onmessage = this.onMessage.bind(this)
  }

  protected abstract onOpen(event: Event): void
  protected abstract onClose(event: CloseEvent): void
  protected abstract onError(event: Event): void
  protected abstract onMessage(event: MessageEvent<unknown>): void
}
